import { Injectable } from '@angular/core';
import { SessionDataProvider } from '@providers/session-data/session-data';
import { ActivatedRouteSnapshot, CanActivate } from '@angular/router';
import { RedirectService } from '@providers/redirect/redirect.service';
import { UserManagerService } from '@providers/user-manager/user-manager.service';
import { StorageService } from '@providers/storage/storage.service';
import { StorageKeys } from '@enums/storage.enum';
import { Subscription, User } from '@models/entities/user';
import { CopnyManagmentService } from '@providers/copny-managment/copny-managment.service';
import { FeatureFlagsService } from '@providers/feature-flags/feature-flags.service';
import { IdpAccessTokenResponse } from '@models/responses/login-response';
import { LoginService } from 'src/endpoints/login.service';
import { FeatureFlagStatus } from '@enums/logistic.enum';
import { environment } from '@environments/environment';
import * as Sentry from "@sentry/angular";

@Injectable()
export class AuthGuardService implements CanActivate {

  constructor(
    private copnyManagment: CopnyManagmentService,
    private featureFlags: FeatureFlagsService,
    private loginService: LoginService,
    private redirect: RedirectService,
    private sessionData: SessionDataProvider,
    private storage: StorageService,
    private userService: UserManagerService
  ) {}

  async canActivate(route: ActivatedRouteSnapshot): Promise<boolean> {
    const [
      user,
      token,
      tokenType,
      refreshToken,
      subscriptionIDP,
      subscriptionsIDP,
      companyData,
      idpAccessToken,
      tokenStats
    ] = await Promise.all([
      this.storage.getMyData(StorageKeys.COOKIE_NAME),
      this.storage.getMyData(StorageKeys.TOKEN),
      this.storage.getMyData(StorageKeys.TOKEN_TYPE),
      this.storage.getMyData(StorageKeys.REFRESH_TOKEN_IDP),
      this.storage.getMyData(StorageKeys.SUBSCRIPTION_IDP),
      this.storage.getMyData(StorageKeys.SUBSCRIPTIONS_IDP),
      this.storage.getMyData(StorageKeys.COMPANY_DATA),
      this.storage.getMyData(StorageKeys.idpAccessToken),
      this.storage.getMyData(StorageKeys.tokenStats)
    ]);

    const isLoginRoute = !!route.data?.login;
    const wantsLogout  = route.queryParamMap.get('logout') === 'true';

    await this.featureFlags.init();

    // ---- Helper internos ----
    const isLogged = Boolean(user && token);

    const hydrateSession = () => {
      this.setSessionData(
        token, tokenType, refreshToken,
        subscriptionIDP, subscriptionsIDP,
        user, idpAccessToken, tokenStats
      );
      this.copnyManagment.company = companyData;
      if (user) {
        Sentry.setUser({ email: user.email, username: user.fullName });
      }
    };

    const hardLogout = async (): Promise<boolean> => {
      if (isLogged) await this.loginService.logoutMethod();
      return isLoginRoute;
    };

    if (this.sessionData.isBetaServer) {
      const ff = await this.featureFlags.isFeatureFlagPresent('environment-change-betades');
      const isBetaUser = await this.verifyBetaUser(user);
      const ffFailed = ff === FeatureFlagStatus.failed;
      if (ffFailed || !isBetaUser) {
        return await hardLogout();
      }
    }

    if (wantsLogout) {
      return await hardLogout();
    }
    if (!isLoginRoute && !isLogged) {
      await this.loginService.logoutMethod();
      return false;
    }
    if (isLogged) {
      hydrateSession();

      if (isLoginRoute) {
        this.redirect.redirectToInit();
        return false;
      }
      return true;
    }
    return true;
  }

  async verifyBetaUser(user: User): Promise<boolean> {
    const betausersFF = await this.featureFlags.isFeatureFlagPresent('beta-users');
    if (betausersFF === FeatureFlagStatus.success) {
      const betausers = await this.featureFlags.evalFeature('beta-users');
      return (betausers ?? []).some(betaUser => betaUser === user?.email);
    }
  }

  setSessionData(
    token: string,
    tokenType: string,
    refresh: string,
    subscription: Subscription,
    subscriptions: Subscription[],
    user: User,
    idpAccessToken: IdpAccessTokenResponse,
    tokenStats: string
  ): void {
    this.sessionData.token = token;
    this.sessionData.tokenType = tokenType;
    this.sessionData.refreshTokenIdp = refresh;
    this.sessionData.subscriptionIdp = subscription;
    this.sessionData.subscriptionsIdp = subscriptions;
    this.sessionData.idpAccessToken = idpAccessToken;
    this.sessionData.tokenStats = tokenStats;
    this.sessionData.isLogged = true;
    this.userService.currentUser = user;
    environment.serverUrl = this.sessionData.subscriptionIdp?.desApiUrl ?? environment.serverUrlAdmin;
    if (this.sessionData.isBetaServer) {
      environment.serverUrl = this.sessionData.subscriptionIdp?.desBetaApiUrl ?? environment.serverUrlAdmin;
    }
    const userEmail = this.userService.email;
    const companyName = this.copnyManagment.companyName;
  }
}
